import axiosInstance from "core/api/axiosInstance";

const texto = (value) => String(value ?? "").trim();
// Este endpoint responde sin PRE_ aunque se consulte la llave preliminar.
// La equivalencia solo se admite en esa respuesta; no cambia la llave de la fila.
const llaveResumenCoincide = (requested, received) =>
  received === requested || (/^PRE_\d+$/.test(requested) && received === requested.slice(4));
function datos(response) {
  const body = response.data;
  if (!body || body.success === false || (body.code != null && body.code !== 0)) {
    throw new Error(body?.message || "No se pudo consultar la conciliación.");
  }
  return body.data;
}
const config = (signal) => ({ signal, timeout: 30000 });
const cancelado = () => {
  const error = new Error("Consulta cancelada");
  error.name = "AbortError";
  return error;
};

// Only retain the authorized concession's summary, never the batch-wide totals.
export async function consultarLlavesConcesion(code, { signal, onProgress = () => {} } = {}) {
  if (!texto(code)) throw new Error("Tu usuario no tiene un código de concesión asignado.");
  const data = datos(
    await axiosInstance.get(
      `/api/conciliation/get-conciliations-detail-by-code/${encodeURIComponent(texto(code))}`,
      config(signal)
    )
  );
  // Sequelize returns [rows, queryMetadata]. Read the rows only, never metadata.rows again.
  const keyRows = Array.isArray(data?.[0]) ? data[0] : data;
  if (
    !Array.isArray(keyRows) ||
    keyRows.some((item) => !item || typeof item !== "object" || !texto(item.key_concil))
  ) {
    throw new Error("La lista de llaves del comercio tiene un formato inválido.");
  }
  const byKey = new Map();
  keyRows.forEach((item) => {
    const key = texto(item.key_concil);
    const id = typeof item.id === "string" || typeof item.id === "number" ? texto(item.id) : "";
    if (byKey.has(key) && byKey.get(key).conciliationId !== (id || undefined))
      throw new Error("El servicio devolvió IDs diferentes para la misma llave.");
    byKey.set(key, {
      ...(id ? { conciliationId: id } : {}),
      concesion: texto(code),
      key_concil: key,
      realName: "",
      countConcil: null,
      ...(item.value_gp != null ? { value_gp: item.value_gp } : {}),
      ...(item.tipo_pago != null ? { tipo_pago: texto(item.tipo_pago).toUpperCase() } : {}),
      ...(item.more?.estaciones ? { peajesEsperados: Object.keys(item.more.estaciones) } : {}),
    });
  });
  const rows = [...byKey.values()];
  const keys = [...byKey.keys()];
  const failed = [];
  const emptyKeys = new Set();
  let next = 0;
  let done = 0;
  onProgress({ done, total: keys.length });
  async function worker() {
    while (next < keys.length) {
      if (signal?.aborted) throw cancelado();
      const index = next++;
      const key = keys[index];
      try {
        const summary = datos(
          await axiosInstance.get(
            `/api/conciliation/get-conciliations-keys-data/${encodeURIComponent(key)}`,
            config(signal)
          )
        );
        if (signal?.aborted) throw cancelado();
        const summaryRows = Array.isArray(summary)
          ? summary
          : summary?.conciliations ?? summary?.data;
        if (!Array.isArray(summaryRows)) throw new Error("Resumen inválido");
        const candidates = summaryRows.filter(
          (row) =>
            texto(row?.code_operator ?? row?.concesion) === texto(code) &&
            llaveResumenCoincide(key, texto(row?.key_concil))
        );
        const exact = candidates.filter((row) => texto(row.key_concil) === key);
        const matches = exact.length ? exact : candidates;
        if (matches.length !== 1)
          throw new Error("El resumen no identifica de forma única la concesión solicitada");
        const row = matches[0];
        if (!llaveResumenCoincide(key, texto(row.key_concil)))
          throw new Error("La llave recibida no coincide");
        const definitive = row.code_operator != null;
        const count = texto(definitive ? row.steps : row.countConcil);
        let more = row.more;
        if (typeof more === "string") {
          try {
            more = JSON.parse(more);
          } catch (_) {
            more = null;
          }
        }
        const name = definitive
          ? texto(more?.concesion) || texto(row.concesion) || texto(row.realName)
          : texto(row.realName);
        const tieneStatus = Object.prototype.hasOwnProperty.call(row, "status");
        const estadoCuadre = tieneStatus
          ? texto(row.status)
          : texto(row.estado_cuadre ?? more?.totales_dia?.estado_cuadre ?? more?.estado_cuadre);
        const summaryId = definitive ? row.id : summary.id?.id ?? summary.id;
        rows[index] = {
          ...rows[index],
          ...(!rows[index].conciliationId &&
          (typeof summaryId === "string" || typeof summaryId === "number")
            ? { conciliationId: texto(summaryId) }
            : {}),
          ...(definitive
            ? {
                detailReference: key,
                documentsConciliationId: texto(row.conciliation_id ?? row.concil_id ?? row.id),
                documentsKeyConcil: texto(row.key_concil),
              }
            : /^\d+$/.test(texto(summaryId))
            ? {
                documentsConciliationId: texto(summaryId),
                documentsKeyConcil: texto(row.key_concil),
              }
            : {}),
          ...(Object.prototype.hasOwnProperty.call(row, "value_final")
            ? { value_final: row.value_final }
            : {}),
          ...(row.value_gp != null || more?.totales_dia?.value_gp != null
            ? { value_gp: row.value_gp ?? more.totales_dia.value_gp }
            : {}),
          realName: name,
          countConcil: /^\d+$/.test(count) ? count : null,
          ...(tieneStatus || estadoCuadre ? { estadoCuadre } : {}),
        };
      } catch (error) {
        if (signal?.aborted || error.name === "AbortError" || error.code === "ERR_CANCELED")
          throw error;
        if ([401, 403].includes(error.response?.status)) throw error;
        failed.push(key);
      }
      // Un resumen ausente no demuestra que la concesión tenga pasos en esta llave.
      if (!(Number(rows[index].countConcil) > 0)) {
        try {
          const detail = datos(
            await axiosInstance.get(
              `/api/conciliation/get-conciliations-detail/${encodeURIComponent(
                key
              )}/${encodeURIComponent(texto(code))}`,
              config(signal)
            )
          );
          if (signal?.aborted) throw cancelado();
          const detailRows = Array.isArray(detail) ? detail : detail?.data;
          if (Array.isArray(detailRows)) {
            if (!detailRows.length) emptyKeys.add(key);
          } else if (!detailRows || typeof detailRows !== "object" || !detailRows.id) {
            throw new Error("El detalle de la llave tiene un formato inválido.");
          }
        } catch (error) {
          if (signal?.aborted || error.name === "AbortError" || error.code === "ERR_CANCELED")
            throw error;
          if ([401, 403].includes(error.response?.status)) throw error;
          if (!failed.includes(key)) failed.push(key);
        }
      }
      done++;
      onProgress({ done, total: keys.length });
    }
  }
  await Promise.all(Array.from({ length: Math.min(3, keys.length) }, worker));
  // Key format is DDMMYYYY + sequence; do not use lexicographic date order.
  const order = (key) => {
    const dateKey = key.replace(/^PRE_(?=\d+$)/, "");
    return /^\d{8}/.test(dateKey)
      ? dateKey.slice(4, 8) + dateKey.slice(2, 4) + dateKey.slice(0, 2) + dateKey.slice(8)
      : dateKey;
  };
  rows.sort((a, b) => order(b.key_concil).localeCompare(order(a.key_concil)));
  return {
    rows: rows.filter((row) => !emptyKeys.has(row.key_concil)),
    failed: failed.filter((key) => !emptyKeys.has(key)),
    total: keys.length,
  };
}
